import React from 'react';
import clsx from 'clsx';
import type { AnimationProps } from 'framer-motion';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
export const MOBILE_COVER_SIZE = 40;
export const TABLET_COVER_SIZE = 48;

export const Container = ({
  children,
  spaceBetween,
}: {
  children: React.ReactNode;
  spaceBetween?: boolean;
}) => {
  return (
    <div
      className={clsx(
        'flex w-full items-center py-3 md:border-b md:border-base200 md:py-4 ',
        spaceBetween && 'justify-between',
      )}
    >
      {children}
    </div>
  );
};

export const Wrapper = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',

  '@tablet': {
    display: 'flex',
  },

  '@tabletLandscape': {
    display: 'flex',
    marginX: 8,
  },
  variants: {
    margin: {
      first: {
        marginLeft: 0,
        '&:after': {
          content: ' ',
          whiteSpace: 'pre',
        },
        '@tablet': {
          marginRight: 6,
          '&:after': {
            content: 'unset',
            whiteSpace: 'unset',
          },
        },
      },
    },
  },
});

export const subTextCSS: CSS = {
  base: 'S',
  fontStyle: 'regular',
  color: '$neutral500',
  lineHeight: '18px',

  '@tablet': {
    base: 'M',
    color: '$neutral500',
    whiteSpace: 'nowrap',
  },

  '@tabletLandscape': {
    base: 'L',
    color: '$neutral500',
  },
};

export const SubText = styled('span', {
  ...subTextCSS,
  variants: {
    black: {
      true: {
        fontStyle: 'semibold',
        color: '$neutral900',
      },
    },

    bold: {
      true: {
        fontStyle: 'semibold',
      },
    },

    time: {
      true: {
        '@tabletLandscape': {
          base: 'M',
          color: '$neutral500',
        },
      },
    },
    cursor: {
      true: {
        cursor: 'pointer',
      },
    },
    forceMobileView: {
      true: {
        base: 'S',
        lineHeight: '18px',
      },
    },
  },
});

export const fadeInAnimation = ({
  duration,
  delay,
}: {
  duration: number;
  delay: number;
}): AnimationProps => ({
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
  transition: {
    duration,
    delay,
  },
});

export const CoverContainer = styled('div', {
  display: 'inline-flex',
  flexShrink: 0,
  width: MOBILE_COVER_SIZE,
  height: MOBILE_COVER_SIZE,
  overflow: 'hidden',
  borderRadius: 4,
  marginRight: 8,

  variants: {
    forceMobileView: {
      true: {
        '@tabletLandscape': {
          display: 'inline-flex',
        },
      },
    },
  },

  '@tablet': {
    display: 'flex',
    alignItems: 'center',
    width: TABLET_COVER_SIZE,
    height: TABLET_COVER_SIZE,
  },

  '@tabletLandscape': {
    display: 'none',
  },
});

export const DisplayWrapper = styled('div', {
  display: 'none',
  alignItems: 'baseline',
  variants: {
    display: {
      inline: {
        display: 'inline-block',
      },
      flex: {
        display: 'flex',
      },
    },
    media: {
      tablet: {
        '@tablet': {
          display: 'inline-block',
        },
      },
      notTablet: {
        display: 'inline-block',

        '@tablet': {
          display: 'none',
        },
      },
      tabletLandscape: {
        '@tabletLandscape': {
          display: 'inline-block',
        },
      },
      notTabletLandscape: {
        display: 'inline-block',

        '@tabletLandscape': {
          display: 'none',
        },
      },
      laptop: {
        '@laptop': {
          display: 'inline-block',
        },
      },
      notLaptop: {
        display: 'inline-block',

        '@laptop': {
          display: 'none',
        },
      },
    },
    forceMobileView: {
      true: {},
    },
  },

  compoundVariants: [
    {
      display: 'flex',
      media: 'tablet',
      css: {
        display: 'none',
        '@tablet': {
          display: 'flex',
        },
        '@tabletLandscape': {
          display: 'none',
        },
      },
    },
    {
      display: 'flex',
      media: 'tablet',
      forceMobileView: true,
      css: {
        display: 'flex',
      },
    },
    {
      display: 'flex',
      media: 'notTablet',
      css: {
        display: 'flex',

        '@tablet': {
          display: 'none',
        },
      },
    },
    {
      display: 'flex',
      media: 'notTabletLandscape',
      css: {
        display: 'flex',

        '@tabletLandscape': {
          display: 'none',
        },
      },
    },

    {
      display: 'inline',
      media: 'notTabletLandscape',
      css: {
        display: 'inline-block',

        '@tabletLandscape': {
          display: 'none',
        },
      },
    },
  ],
});

export const GoldenEggWrapper = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  height: '12px',
  verticalAlign: 'middle',
  transform: 'scale(0.7)',
  svg: {
    filter: 'drop-shadow(0px 2px 5px rgba(217, 166, 37, 0.6))',
  },
});

export const PlaylistLink = styled(AccessibleLink, {
  display: 'inline-flex',

  '@tablet': {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
